import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sheetViewAPI } from '../api/client';

const SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'];
const PAGE = 40;
const byName = (a, b) => a.localeCompare(b, 'uk', { sensitivity: 'base', numeric: true });
const colLetter = (i) => String.fromCharCode(65 + i);

const BLOCK_ROWS = 17;
const ROW_H = 26;                      // висота рядка фіксована (див. .sv-grid th/td)
const rhOf = (z) => Math.max(10, Math.round(ROW_H * z));   // цілі пікселі — без дробових зсувів при масштабі
const BUFFER = 3;                      // скільки блоків малюємо за межами екрана

// Ряди одного блоку з нумерацією, як у Google-таблиці: блок = 17 рядів
function blockRows(b, ncols, start) {
  const rows = [];
  const push = (r) => rows.push({ n: start + rows.length + 1, ...r });
  {
    const inStock = new Set();
    b.suppliers.forEach(s => Object.entries(s.sizes).forEach(([sz, q]) => { if (q > 0) inStock.add(sz); }));
    const sup = (i) => b.suppliers[i - 2];
    push({ cells: [null, { text: b.sku, cls: 'sv-art' }] });
    push({ cells: [] });
    push({ cells: [null, null, ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: 'ФОТО', link: sup(k + 2).link, cls: 'sv-lnk' } : null)] });
    push({ cells: [null, null, ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: sup(k + 2).name, link: sup(k + 2).supplier_link, cls: 'sv-lnk' } : null)] });
    push({ cells: [{ text: b.name, cls: 'sv-name' }, { text: 'Размеры', cls: 'sv-head' },
      ...Array.from({ length: ncols - 2 }, (_, k) => sup(k + 2) ? { text: sup(k + 2).price != null ? Math.round(sup(k + 2).price) : '—', link: sup(k + 2).table_link, cls: 'sv-price' } : null)] });
    SIZES.forEach((sz, ri) => {
      push({
        photo: ri === 0 ? b.photo || true : undefined,
        cells: [undefined, { text: sz, cls: inStock.has(sz) ? 'sv-size sv-size--on' : 'sv-size' },
          ...Array.from({ length: ncols - 2 }, (_, k) => { const s = sup(k + 2); return s && s.sizes[sz] > 0 ? { text: s.sizes[sz] } : null; })],
      });
    });
    push({ cells: [] });
  }
  return rows;
}

// Порожній відступ замість блоків поза екраном
const Gap = React.memo(function Gap({ rows, cols, rh }) {
  return <tbody><tr style={{ height: rows * BLOCK_ROWS * rh }}><td colSpan={cols} style={{ padding: 0, border: 'none' }} /></tr></tbody>;
});

// Один блок = окремий <tbody>; React.memo — при підвантаженні нових блоків старі не перемальовуються
const BlockBody = React.memo(function BlockBody({ b, ncols, start, rh, selRow, selCol, selCellCol, onRowClick }) {
  const rows = useMemo(() => blockRows(b, ncols, start), [b, ncols, start]);
  const colOn = (c) => selCol === c;
  return (
    <tbody>
      {rows.map(r => (
        <tr key={r.n} className={selRow === r.n && selCellCol === -1 ? 'sv-row--sel' : ''}>
          <th className={`sv-rowh${selRow === r.n ? ' sv-rowh--on' : ''}`}
              onClick={(e) => { e.stopPropagation(); onRowClick(r.n); }}>{r.n}</th>
          {r.cells.map((cell, c) => {
            if (cell === undefined && c === 0) {
              if (r.photo === undefined) return null;
              return (
                <td key={c} rowSpan={SIZES.length} data-r={r.n} data-c={c}
                    className={`sv-c sv-photo${colOn(c) ? ' sv-c--col' : ''}`}>
                  {typeof r.photo === 'string' ? <img src={r.photo} alt="" loading="lazy" decoding="async" style={{ height: SIZES.length * rh }} /> : null}
                </td>
              );
            }
            const on = selRow === r.n && selCellCol === c;
            const cls = ['sv-c', cell?.cls || '', colOn(c) ? 'sv-c--col' : '', on ? 'sv-c--cell' : ''].join(' ');
            return (
              <td key={c} data-r={r.n} data-c={c} className={cls}>
                {cell?.link ? <a href={cell.link} target="_blank" rel="noreferrer">{cell.text}</a> : cell?.text}
              </td>
            );
          })}
          {Array.from({ length: Math.max(0, ncols - r.cells.length) }, (_, k) => {
            const c = r.cells.length + k;
            return <td key={`e${c}`} data-r={r.n} data-c={c} className={`sv-c${colOn(c) ? ' sv-c--col' : ''}`} />;
          })}
        </tr>
      ))}
    </tbody>
  );
});

export default function SheetViewTab({ standalone = false }) {
  const [tabs, setTabs] = useState([]);
  const [sel3, setSel3] = useState(() => { try { return JSON.parse(localStorage.getItem('sv_sel')) || {}; } catch { return {}; } });
  const tab = sel3.model || '';
  const [blocks, setBlocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);       // {row, col}: null = не задано
  const [full, setFull] = useState(false);
  const [menu, setMenu] = useState(false);
  const isFull = full || standalone;
  const [zoom, setZoomRaw] = useState(() => { try { return parseFloat(localStorage.getItem('sv_zoom')) || 1; } catch { return 1; } });
  const zoomRef = useRef(zoom);
  const setZoom = useCallback((z) => {
    z = Math.min(2.5, Math.max(0.3, Math.round(z * 100) / 100));
    zoomRef.current = z; setZoomRaw(z);
    try { localStorage.setItem('sv_zoom', String(z)); } catch {}
  }, []);
  const bodyRef = useRef(null);
  const [bars, setBars] = useState(true);   // верхня і нижня панелі: ховаються при прокрутці вниз
  const lastTop = useRef(0);
  const moreRef = useRef(null);
  const [range, setRange] = useState({ from: 0, to: 8 });   // які блоки зараз реально малюємо
  const rafRef = useRef(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    sheetViewAPI.tabs().then(r => setTabs(r.data));
  }, []);

  // сезон → бренд → модель: підбираємо коректний вибір, якщо збережений вже не існує
  const seasons = useMemo(() => [...new Set(tabs.map(t => t.season))], [tabs]);
  const brandsOf = useCallback((se) => {
    const m = {};
    tabs.filter(t => t.season === se).forEach(t => { m[t.brand] = (m[t.brand] || 0) + t.count; });
    return Object.entries(m).sort((a, b) => byName(a[0], b[0]));
  }, [tabs]);
  const modelsOf = useCallback((se, br) =>
    tabs.filter(t => t.season === se && t.brand === br).sort((a, b) => byName(a.model, b.model)), [tabs]);

  useEffect(() => {
    if (!tabs.length) return;
    let { season, brand, model } = sel3;
    if (!seasons.includes(season)) season = seasons[0];
    const bs = brandsOf(season);
    if (!bs.find(b => b[0] === brand)) brand = bs[0][0];
    const ms = modelsOf(season, brand);
    if (!ms.find(m => m.model === model)) model = ms[0].model;
    if (season !== sel3.season || brand !== sel3.brand || model !== sel3.model) setSel3({ season, brand, model });
  }, [tabs, sel3, seasons, brandsOf, modelsOf]);

  const pickSeason = (se) => { const b = brandsOf(se)[0][0]; setSel3({ season: se, brand: b, model: modelsOf(se, b)[0].model }); };
  const pickBrand = (b) => setSel3({ season: sel3.season, brand: b, model: modelsOf(sel3.season, b)[0].model });
  const pickModel = (m) => setSel3({ ...sel3, model: m });

  const load = useCallback((key, offset, se, br) => {
    setLoading(true);
    sheetViewAPI.blocks(key, { offset, limit: PAGE, season: se, brand: br }).then(r => {
      setTotal(r.data.total);
      setBlocks(prev => offset === 0 ? r.data.blocks : [...prev, ...r.data.blocks]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!tab) return;
    try { localStorage.setItem('sv_sel', JSON.stringify(sel3)); } catch {}
    setBlocks([]); setSel(null); setRange({ from: 0, to: 8 });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    load(tab, 0, sel3.season, sel3.brand);
  }, [sel3.season, sel3.brand, tab, load]);

  useEffect(() => {
    // на iOS Fullscreen API немає — там працює лише CSS-режим (full керується кнопкою)
    const h = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  // Масштаб: щипок двома пальцями на телефоні і Ctrl+колесо на комп'ютері
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    let d0 = 0, z0 = 1;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const ts = (e) => { if (e.touches.length === 2) { d0 = dist(e.touches); z0 = zoomRef.current; } };
    const tm = (e) => { if (e.touches.length === 2 && d0) { e.preventDefault(); setZoom(z0 * dist(e.touches) / d0); } };
    const te = () => { d0 = 0; };
    const wh = (e) => { if (e.ctrlKey) { e.preventDefault(); setZoom(zoomRef.current * (e.deltaY < 0 ? 1.1 : 0.9)); } };
    el.addEventListener('touchstart', ts, { passive: true });
    el.addEventListener('touchmove', tm, { passive: false });
    el.addEventListener('touchend', te);
    el.addEventListener('wheel', wh, { passive: false });
    return () => {
      el.removeEventListener('touchstart', ts); el.removeEventListener('touchmove', tm);
      el.removeEventListener('touchend', te); el.removeEventListener('wheel', wh);
    };
  }, [setZoom]);

  // Поки таблиця на екрані — сторінка під нею не прокручується (інакше на краях таблиці «смикається» уся сторінка)
  useEffect(() => {
    if (!isFull) return;
    document.documentElement.classList.add('sv-lock');
    return () => document.documentElement.classList.remove('sv-lock');
  }, [isFull]);

  // Автодовантаження: коли кінець списку наближається до екрана — тягнемо наступну порцію
  useEffect(() => {
    const el = moreRef.current;
    if (!el || loading || blocks.length >= total) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) load(tab, blocks.length, sel3.season, sel3.brand);
    }, { root: bodyRef.current, rootMargin: '0px 0px 3000px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [blocks.length, total, loading, tab, sel3.season, sel3.brand, load]);

  // Віртуалізація, як у Google-таблиці: у DOM лише блоки, що на екрані (± запас), решта — порожній відступ
  const updateRange = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const rh = rhOf(zoomRef.current);
    const bh = BLOCK_ROWS * rh;
    const top = el.scrollTop - rh;
    const bottom = el.scrollTop + el.clientHeight - rh;
    const from = Math.max(0, Math.floor(top / bh) - BUFFER);
    const to = Math.max(0, Math.ceil(bottom / bh) + BUFFER);
    setRange(r => (r.from === from && r.to === to ? r : { from, to }));
  }, []);

  useEffect(() => { updateRange(); }, [blocks.length, zoom, bars, updateRange]);

  const onScroll = (e) => {
    if (!rafRef.current) rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; updateRange(); });
    const t = e.currentTarget.scrollTop;
    const d = t - lastTop.current;
    if (t < 30) setBars(true);
    else if (d > 12) { setBars(false); setMenu(false); }
    else if (d < -12) setBars(true);
    if (Math.abs(d) > 12 || t < 30) lastTop.current = t;
  };

  const toggleFull = () => {
    if (full) {
      setFull(false);
      if (document.fullscreenElement) document.exitFullscreen?.();
    } else {
      setFull(true);
    }
  };

  const [bodyW, setBodyW] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !window.ResizeObserver) return;
    const ro = new ResizeObserver(() => setBodyW(el.clientWidth));
    ro.observe(el); setBodyW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const rh = rhOf(zoom);
  const colW = (i) => Math.round((i === 0 ? 270 : i === 1 ? 84 : 92) * zoom);
  const ncols = useMemo(() => {
    const fromData = 2 + Math.max(0, ...blocks.map(b => b.suppliers.length));
    const fill = Math.ceil((bodyW - (46 + 270 + 84) * zoom) / (92 * zoom)) + 2;   // як у Google: порожні колонки до краю екрана
    return Math.max(6, fromData, Math.min(fill, 60));
  }, [blocks, bodyW, zoom]);
  const tableW = Math.round(46 * zoom) + Array.from({ length: ncols }, (_, i) => colW(i)).reduce((a, b) => a + b, 0);
  const onRowClick = useCallback((n) => setSel({ row: n, col: -1 }), []);
  const onBody = (e) => {
    const t = e.target.closest('[data-r]');
    if (t) setSel({ row: +t.dataset.r, col: +t.dataset.c });
  };

  const colOn = (c) => sel && sel.col === c && sel.row === -1;

  return (
    <div className={`sv-wrap${isFull ? ' sv-wrap--full' : ''}`} ref={wrapRef}>
      <div className={`sv-bar${bars ? '' : ' sv-hide-top'}`}>
        <span className="sv-bar-info">{sel3.season} › {sel3.brand} › {tab} · {blocks.length} з {total}</span>
        <span className="sv-zoom">
          <button onClick={() => setZoom(zoom - 0.1)}>−</button>
          <button className="sv-zoom-val" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button onClick={() => setZoom(zoom + 0.1)}>+</button>
        </span>
        {!standalone && <button className="sv-fs" onClick={toggleFull}>{full ? '✕ Вийти з повного екрана' : '⛶ На весь екран'}</button>}
      </div>
      <div className="sv-body" ref={bodyRef} onClick={onBody} onScroll={onScroll}>
        <table className="sv-grid" style={{ "--rh": rh + "px", "--z": zoom, fontSize: Math.max(6, 13 * zoom), width: tableW }}>
          <colgroup>
            <col style={{ width: Math.round(46 * zoom) }} />
            {Array.from({ length: ncols }, (_, i) => <col key={i} style={{ width: colW(i) }} />)}
          </colgroup>
          <thead>
            <tr>
              <th className="sv-corner" onClick={() => setSel(null)} />
              {Array.from({ length: ncols }, (_, c) => (
                <th key={c} className={`sv-colh${colOn(c) || (sel && sel.col === c && sel.row > 0) ? ' sv-colh--on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSel({ row: -1, col: c }); }}>{colLetter(c)}</th>
              ))}
            </tr>
          </thead>
          {range.from > 0 && <Gap rows={Math.min(range.from, blocks.length)} cols={ncols + 1} rh={rh} />}
          {blocks.slice(range.from, range.to + 1).map((b, k) => {
            const i = range.from + k;
            const start = i * BLOCK_ROWS;
            const inBlock = sel && sel.row > start && sel.row <= start + BLOCK_ROWS;
            return (
              <BlockBody key={b.sku} b={b} ncols={ncols} start={start} rh={rh}
                         selRow={inBlock ? sel.row : 0}
                         selCol={sel && sel.row === -1 ? sel.col : -1}
                         selCellCol={inBlock ? sel.col : -2}
                         onRowClick={onRowClick} />
            );
          })}
          {range.to + 1 < blocks.length && <Gap rows={blocks.length - range.to - 1} cols={ncols + 1} rh={rh} />}
        </table>
        {loading && <div className="sv-note">Завантаження…</div>}
        {blocks.length < total && <div ref={moreRef} className="sv-more-sentinel" />}
      </div>
      <button className="sv-fab" title="Вибір листа" onClick={() => setMenu(true)}>☰</button>
      {menu && (
        <div className="sv-modal" onClick={() => setMenu(false)}>
          <div className="sv-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="sv-modal-head">
              <b>Вибір листа</b>
              <button className="sv-fs" onClick={() => setMenu(false)}>✕</button>
            </div>
            <div className="sv-cols">
              <div className="sv-col">
                <div className="sv-col-title">Сезон</div>
                {seasons.map(se => (
                  <div key={se} className={`sv-opt${se === sel3.season ? ' sv-opt--on' : ''}`} onClick={() => pickSeason(se)}>{se || 'Без сезону'}</div>
                ))}
              </div>
              <div className="sv-col">
                <div className="sv-col-title">Бренд</div>
                {brandsOf(sel3.season).map(([br, c]) => (
                  <div key={br} className={`sv-opt${br === sel3.brand ? ' sv-opt--on' : ''}`} onClick={() => pickBrand(br)}>
                    {br || 'Без бренду'} <span className="sv-count">{c}</span>
                  </div>
                ))}
              </div>
              <div className="sv-col">
                <div className="sv-col-title">Модель</div>
                {modelsOf(sel3.season, sel3.brand).map(t => (
                  <div key={t.model} className={`sv-opt${t.model === tab ? ' sv-opt--on' : ''}`}
                       onClick={() => { pickModel(t.model); setMenu(false); }}>
                    {t.model} <span className="sv-count">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
